import { useEffect, useRef, useState, useCallback } from 'react';
import {
  useAudioEngine,
  useCamera,
  useGestureLoop,
  useHandDetection,
  useTransport,
  ensureTransportSingletons,
} from '@hooks/index';
import {
  applyUrlConfigToStore,
  parseTuningFromUrl,
  useHandStore,
  useSettingsStore,
  useUiStore,
} from '@store/index';
import { HandOverlay } from './HandOverlay';
import { StringsOverlay } from './StringsOverlay';
import { GestureHUD } from './GestureHUD';
import { SettingsTrigger } from './SettingsTrigger';
import { StartOverlay } from './StartOverlay';
import { CalibrationTrigger, CalibrationWizard } from './CalibrationWizard';
import { TuningTrigger } from '@features/tuning';
import { SynthTrigger } from '@features/synth';
import { TunerTrigger, TunerView } from '@features/tuner';
import { TransportTrigger, TransportQuickActions } from '@features/transport';
import './CameraStage.css';

/**
 * Instrument stage. Flow:
 *   1. Mount — only <video> + <StartOverlay> render; camera + MediaPipe
 *      stay idle.
 *   2. User taps "Tap to start" → onStart() synchronously initializes
 *      the audio context AND flips `started = true`.
 *   3. Setting `started = true` enables useCamera + useHandDetection.
 *   4. StartOverlay watches (started, audioReady, cameraStreaming) and
 *      shows a "Ready to play" confirmation before dismissing.
 */
export function CameraStage() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const mirrorCamera = useSettingsStore((s) => s.mirrorCamera);
  const showHandOverlay = useSettingsStore((s) => s.showHandOverlay);
  const started = useUiStore((s) => s.started);
  const setStarted = useUiStore((s) => s.setStarted);
  const tunerOpen = useUiStore((s) => s.tunerOpen);
  const setTunerOpen = useUiStore((s) => s.setTunerOpen);
  const cameraStatus = useHandStore((s) => s.cameraStatus);
  const cameraError = useHandStore((s) => s.cameraError);

  // Camera + hand detection pause while the tuner owns the mic. We
  // tear down the video stream so iOS Safari doesn't fight the mic for
  // resources; the video element is still mounted so the camera can
  // resume instantly when the tuner closes.
  const captureEnabled = started && !tunerOpen;

  const [aspect, setAspect] = useState(16 / 9);
  const [calibOpen, setCalibOpen] = useState(false);

  // Deep-link support:
  //   · `?preset=…&root=C&octave=3&lowest=2&strings=17&…` applies a
  //     specific tuning to set 0, so a teacher can hand a student one
  //     link that loads the exact scale they need.
  //   · `#tuner` or `?tuner` pre-selects tuner mode so the app boots
  //     straight into tuning verification after the start tap.
  // Both run once on mount; the hash (tuner flag) also stays in sync
  // with `tunerOpen` via the effect below so the current state is
  // shareable by copy-pasting the URL bar.
  useEffect(() => {
    const cfg = parseTuningFromUrl();
    if (cfg) applyUrlConfigToStore(cfg);
    const wantsTuner =
      window.location.hash === '#tuner' ||
      window.location.search.includes('tuner=1') ||
      // plain `?tuner` (no value) — URLSearchParams.has catches this.
      new URLSearchParams(window.location.search).has('tuner');
    if (wantsTuner) setTunerOpen(true);
  }, [setTunerOpen]);
  useEffect(() => {
    const want = tunerOpen ? '#tuner' : '';
    if (window.location.hash !== want) {
      const url = new URL(window.location.href);
      url.hash = want;
      // replaceState — deep-linking shouldn't clog the back stack.
      window.history.replaceState(null, '', url.toString());
    }
  }, [tunerOpen]);

  const { engineRef, ensureStarted } = useAudioEngine();
  const { step } = useGestureLoop(engineRef);
  useTransport(engineRef);

  useCamera(videoRef, captureEnabled);

  const onFrame = useCallback(
    (ts: number) => step(ts),
    [step],
  );

  useHandDetection(videoRef, captureEnabled, onFrame);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const update = () => {
      if (video.videoWidth && video.videoHeight) {
        setAspect(video.videoWidth / video.videoHeight);
      }
    };
    video.addEventListener('loadedmetadata', update);
    video.addEventListener('resize', update);
    update();
    return () => {
      video.removeEventListener('loadedmetadata', update);
      video.removeEventListener('resize', update);
    };
  }, []);

  /**
   * Fires SYNCHRONOUSLY inside the StartOverlay's pointerdown/click
   * handler — AudioContext is created + resumed in the user gesture
   * stack, and camera / detection are enabled via `setStarted(true)`.
   */
  const handleStart = useCallback(() => {
    const engine = ensureStarted();
    // Transport/Metronome/Recorder must exist in the SAME user-gesture
    // tick as the AudioContext — otherwise the first ~120ms of plucks
    // race against a still-null transport and silently bypass quantize.
    // Matches Air Drum Set's eager pattern (synth + transport built
    // together at module load).
    ensureTransportSingletons(engine);
    setStarted(true);
  }, [ensureStarted, setStarted]);

  // Resume on user gestures (Chrome sometimes suspends after tab blur).
  // Suspend fully on page hide so screen-lock doesn't machine-gun plucks.
  useEffect(() => {
    if (!started) return;
    const resume = () => ensureStarted();
    const onVisibility = () => {
      if (document.hidden) {
        engineRef.current?.suspend();
      } else {
        ensureStarted();
      }
    };
    const onPageHide = () => engineRef.current?.suspend();
    window.addEventListener('pointerdown', resume, { passive: true });
    window.addEventListener('touchstart', resume, { passive: true });
    window.addEventListener('keydown', resume);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      window.removeEventListener('pointerdown', resume);
      window.removeEventListener('touchstart', resume);
      window.removeEventListener('keydown', resume);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPageHide);
    };
  }, [started, ensureStarted, engineRef]);

  return (
    <div className="stage-wrap">
      <div className="stage" style={{ aspectRatio: String(aspect) }}>
        <video
          ref={videoRef}
          className="stage__video"
          style={{
            transform: mirrorCamera ? 'scaleX(-1)' : undefined,
            // Hide the video feed entirely while the tuner owns the
            // stage — the dark stage background shows through and
            // keeps the look clean behind the strings + pitch trail.
            visibility: tunerOpen ? 'hidden' : 'visible',
          }}
          autoPlay
          playsInline
          muted
        />
        {/* Hand landmarks — mirrored to match the video feed. Hidden
            in tuner mode since hand tracking is paused. */}
        {started && showHandOverlay && !tunerOpen && (
          <div className={`stage__overlay${mirrorCamera ? ' stage__overlay--mirrored' : ''}`}>
            <HandOverlay />
          </div>
        )}
        {/* Strings + labels — NOT mirrored so text reads normally. */}
        {started && (
          <div className="stage__overlay">
            <StringsOverlay />
          </div>
        )}
        {started && !tunerOpen && <GestureHUD />}
        {started && !tunerOpen && <CalibrationTrigger onOpen={() => setCalibOpen(true)} />}
        {started && calibOpen && <CalibrationWizard onClose={() => setCalibOpen(false)} />}
        {/* Tuning + Synth triggers stay visible in tuner mode so the
            player can adjust string pitches or timbre without leaving
            the tuner — the drawers (z-index 40/50) slide in on top of
            the tuner (z-index 25). Tuner trigger itself is hidden
            while open since the × button on the tuner closes it. */}
        {started && <TuningTrigger />}
        {started && <SynthTrigger />}
        {started && !tunerOpen && <TunerTrigger />}
        {started && <TransportTrigger />}
        {started && <TransportQuickActions />}
        <SettingsTrigger />
        {tunerOpen && <TunerView onClose={() => setTunerOpen(false)} />}
        {started && (cameraStatus === 'denied' || cameraStatus === 'error') && (
          <div className="stage__curtain">
            <p>
              {cameraStatus === 'denied'
                ? 'Camera permission denied.'
                : 'Camera error.'}
            </p>
            {cameraError && (
              <p style={{ fontSize: '.78rem', opacity: 0.75, maxWidth: '28rem' }}>
                {cameraError}
              </p>
            )}
            <button
              type="button"
              className="stage__retry"
              onClick={() => window.location.reload()}
            >
              Reload
            </button>
          </div>
        )}
        <StartOverlay onStart={handleStart} />
      </div>
    </div>
  );
}
